import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { useCan } from "../../_lib/rbac/use-can";
import { Card, PageRoot } from "../../_lib/stat-primitives";
import {
  selectActiveProgram,
  useMyPrograms,
} from "../../_lib/use-active-program";
import { useProgramDirectory } from "../../_lib/use-program-directory";
import { linkToEpocusPerformer } from "../../links";
import { displayName, formatPct } from "../_lib/admin-metrics";
import {
  CardHeading,
  CardNote,
  EmptyRow,
  KpiCard,
  KpiGrid,
  PageTitle,
  ScrollTable,
  ShareBar,
  StatusPill,
  TABLE_CELL_STYLE,
} from "../_lib/admin-primitives";
import { useEpocusProgramCredentialingQuery } from "./_lib/__generated__/credentialing-operations.gql";
import {
  credentialStatusLabel,
  credentialStatusTone,
} from "./_lib/credential-status";
import {
  type EditableCredential,
  EditCredentialSheet,
} from "./_lib/edit-credential-sheet";

export const EpocusCredentialingPage: FC = () => {
  const { programs, loading: programsLoading } = useMyPrograms();
  const program = selectActiveProgram(programs);
  const programId = program?.id ?? null;
  const canManage = useCan("manageCredentials");

  const { data, loading, error } = useEpocusProgramCredentialingQuery({
    variables: { programId: programId ?? "" },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  const { reviewers } = useProgramDirectory(programId);
  const [editing, setEditing] = useState<EditableCredential | null>(null);

  const credentialing = data?.epocusProgramCredentialing;

  return (
    <PageRoot>
      <PageTitle
        title="Credentialing & supervision"
        subtitle="Performer credential status and oversight"
      />

      {!credentialing && (programsLoading || loading) ? (
        <Box style={{ display: "flex", justifyContent: "center", padding: 32 }}>
          <CircularProgress aria-label="Loading credentialing" />
        </Box>
      ) : !programId ? (
        <Card>
          <CardNote>Couldn't determine your program.</CardNote>
        </Card>
      ) : !credentialing && error ? (
        <Card>
          <CardNote>Couldn't load credentialing.</CardNote>
        </Card>
      ) : credentialing ? (
        <>
          <KpiGrid>
            <KpiCard
              label="Credentialed"
              value={String(credentialing.counts.credentialed)}
              caption="independent scanning"
            />
            <KpiCard
              label="In progress"
              value={String(credentialing.counts.inProgress)}
              caption="toward credential"
            />
            <KpiCard
              label="Provisional"
              value={String(credentialing.counts.provisional)}
              caption="supervised only"
            />
            <KpiCard
              label="Program QA average"
              value={formatPct(credentialing.programQaAveragePct)}
              caption={`rolling ${credentialing.qaAverageWindowDays} days`}
            />
          </KpiGrid>

          <Card>
            <CardHeading
              title="By performer"
              hint={`QA average over ${credentialing.qaAverageWindowDays} days`}
            />
            <ScrollTable
              columns={[
                "Performer",
                "Credential status",
                "Supervisor",
                "Supervised studies",
                "QA average",
                "",
              ]}
            >
              {credentialing.rows.length === 0 ? (
                <EmptyRow colSpan={6}>
                  No performer has scanned in this program yet.
                </EmptyRow>
              ) : (
                credentialing.rows.map((row) => {
                  const name = displayName(row.name, "performer");
                  return (
                    <tr key={row.performerId}>
                      <td style={TABLE_CELL_STYLE}>
                        <Link
                          to={linkToEpocusPerformer(row.performerId)}
                          style={{
                            fontSize: rem(13),
                            fontWeight: 600,
                            color: EPOCUS_TEXT_PRIMARY,
                            textDecoration: "none",
                          }}
                        >
                          {name}
                        </Link>
                        <Typography
                          style={{
                            fontSize: rem(12),
                            color: EPOCUS_TEXT_SECONDARY,
                          }}
                        >
                          {row.unit ?? "No unit recorded"}
                        </Typography>
                      </td>
                      <td style={TABLE_CELL_STYLE}>
                        <StatusPill
                          tone={credentialStatusTone(row.status)}
                          label={credentialStatusLabel(row.status)}
                        />
                      </td>
                      <td style={TABLE_CELL_STYLE}>
                        {row.supervisorName ??
                          (row.supervisorId === null
                            ? "None required"
                            : displayName(null, "reviewer"))}
                      </td>
                      <td style={TABLE_CELL_STYLE}>{row.supervisedStudies}</td>
                      <td style={TABLE_CELL_STYLE}>
                        <ShareBar
                          value={row.qaAveragePct ?? null}
                          label={formatPct(row.qaAveragePct)}
                        />
                      </td>
                      <td style={{ ...TABLE_CELL_STYLE, textAlign: "right" }}>
                        {canManage && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setEditing({
                                performerId: row.performerId,
                                performerName: name,
                                status: row.status,
                                supervisorId: row.supervisorId ?? null,
                                supervisorName: row.supervisorName ?? null,
                                unit: row.unit ?? null,
                              })
                            }
                          >
                            Edit
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </ScrollTable>
            <CardNote>
              A performer with no credential row reads as Provisional —
              unassessed, not cleared. Select a performer for their full record.
            </CardNote>
          </Card>
        </>
      ) : null}

      {programId !== null && (
        <EditCredentialSheet
          programId={programId}
          credential={editing}
          supervisors={reviewers}
          onClose={() => setEditing(null)}
        />
      )}
    </PageRoot>
  );
};
